import React, { useState, useMemo } from 'react'
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  FileText,
  Clock,
  AlertCircle,
  Download,
  Calendar,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  Users,
  CheckCircle2
} from 'lucide-react'
import { useApp } from '../context/AppContext'

const periodOptions = [
  { value: 'week', label: 'This Week' },
  { value: 'lastweek', label: 'Last Week' },
  { value: 'month', label: 'This Month' },
  { value: 'lastmonth', label: 'Last Month' },
  { value: '90d', label: 'Last 90 Days' },
  { value: 'year', label: 'This Year' },
  { value: 'all', label: 'All Time' },
]

export default function Reports() {
  const { invoices, clients, getClientById } = useApp()
  const [period, setPeriod] = useState('month')

  // Helper to safely parse Firestore Timestamps or other date formats
  const parseDate = (dateValue) => {
    if (!dateValue) return null
    if (dateValue.toDate) return dateValue.toDate()
    if (dateValue.seconds) return new Date(dateValue.seconds * 1000)
    const date = new Date(dateValue)
    return isNaN(date.getTime()) ? null : date
  }

  // Get date range based on period
  const getDateRange = (periodValue) => {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

    switch (periodValue) {
      case 'week': {
        const dayOfWeek = today.getDay()
        const startOfWeek = new Date(today)
        startOfWeek.setDate(today.getDate() - dayOfWeek)
        return { start: startOfWeek, end: now, label: 'This Week' }
      }
      case 'lastweek': {
        const dayOfWeek = today.getDay()
        const startOfLastWeek = new Date(today)
        startOfLastWeek.setDate(today.getDate() - dayOfWeek - 7)
        const endOfLastWeek = new Date(startOfLastWeek)
        endOfLastWeek.setDate(startOfLastWeek.getDate() + 6)
        endOfLastWeek.setHours(23, 59, 59, 999)
        return { start: startOfLastWeek, end: endOfLastWeek, label: 'Last Week' }
      }
      case 'month': {
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
        return { start: startOfMonth, end: now, label: 'This Month' }
      }
      case 'lastmonth': {
        const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0)
        endOfLastMonth.setHours(23, 59, 59, 999)
        return { start: startOfLastMonth, end: endOfLastMonth, label: 'Last Month' }
      }
      case '90d': {
        const start = new Date(today)
        start.setDate(today.getDate() - 90)
        return { start, end: now, label: 'Last 90 Days' }
      }
      case 'year': {
        const startOfYear = new Date(now.getFullYear(), 0, 1)
        return { start: startOfYear, end: now, label: 'This Year' }
      }
      default:
        return { start: null, end: null, label: 'All Time' }
    }
  }

  const currentRange = getDateRange(period)
  const previousRange = useMemo(() => {
    if (period === 'week') return getDateRange('lastweek')
    if (period === 'month') return getDateRange('lastmonth')
    return null
  }, [period])

  // Extract all individual payments from all invoices
  const allPayments = useMemo(() => {
    const payments = []
    invoices.forEach(inv => {
      // Handle explicit payments array
      if (inv.payments && Array.isArray(inv.payments)) {
        inv.payments.forEach(p => {
          payments.push({
            id: `${inv.id}-${p.date?.seconds || Date.now()}`,
            amount: p.amount,
            date: parseDate(p.date),
            clientId: inv.clientId,
            invoiceId: inv.id,
            invoiceNumber: inv.invoiceNumber
          })
        })
      }
      // Fallback for older invoices with status 'paid' but no payments array
      else if (inv.status === 'paid' && (inv.paidAt || inv.createdAt)) {
        payments.push({
          id: `legacy-${inv.id}`,
          amount: inv.total,
          date: parseDate(inv.paidAt || inv.createdAt),
          clientId: inv.clientId,
          invoiceId: inv.id,
          invoiceNumber: inv.invoiceNumber
        })
      }
    })
    return payments
  }, [invoices])

  // Filter payments by date range
  const paymentsByRange = (p, range) => {
    if (!range.start) return true
    if (!p.date) return false
    return p.date >= range.start && p.date <= range.end
  }

  // Filter invoices by creation date (accrual)
  const filterInvoicesByRange = (inv, range) => {
    if (!range.start) return true
    const invDate = parseDate(inv.createdAt)
    return invDate && invDate >= range.start && invDate <= range.end
  }

  // Calculate metrics for a period
  const calculatePeriodMetrics = (range) => {
    if (!range) return null

    const periodPayments = allPayments.filter(p => paymentsByRange(p, range))
    const periodInvoices = invoices.filter(inv => filterInvoicesByRange(inv, range))

    const totalRevenue = periodPayments.reduce((sum, p) => sum + p.amount, 0)
    const totalBilled = periodInvoices.reduce((sum, inv) => sum + inv.total, 0)

    // Outstanding and Overdue are "at this moment" snapshots, not period-dependent
    // but pending count etc within period can be calculated from filtered invoices
    const pendingCount = periodInvoices.filter(inv => ['sent', 'viewed', 'partial'].includes(inv.status)).length
    const overdueCount = periodInvoices.filter(inv => inv.status === 'overdue').length

    return {
      revenue: totalRevenue,
      billed: totalBilled,
      pendingCount,
      overdueCount,
      invoiceCount: periodInvoices.length,
      collectionRate: totalBilled > 0 ? (totalRevenue / totalBilled) * 100 : 0
    }
  }

  const metrics = calculatePeriodMetrics(currentRange)
  const prevMetrics = previousRange ? calculatePeriodMetrics(previousRange) : null

  // Global "At a Glance" - ignores period for Outstanding/Overdue
  const globalMetrics = useMemo(() => {
    const outstanding = invoices
      .filter(inv => ['sent', 'viewed', 'partial', 'overdue'].includes(inv.status))
      .reduce((sum, inv) => sum + (inv.total - (inv.partialAmount || 0)), 0)

    const overdue = invoices
      .filter(inv => inv.status === 'overdue')
      .reduce((sum, inv) => sum + (inv.total - (inv.partialAmount || 0)), 0)

    const totalInvoices = invoices.length
    const paidCount = invoices.filter(inv => inv.status === 'paid').length

    return { outstanding, overdue, totalInvoices, paidCount }
  }, [invoices])

  const getChange = (current, previous) => {
    if (!previous || previous === 0) return null
    const change = ((current - previous) / previous) * 100
    return change.toFixed(1)
  }

  // Chart Breakdown
  const breakdownData = useMemo(() => {
    const periodPayments = allPayments.filter(p => paymentsByRange(p, currentRange))

    if (period === 'week' || period === 'lastweek') {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      const data = days.map(day => ({ label: day, revenue: 0, count: 0 }))
      periodPayments.forEach(p => {
        const dayIndex = p.date.getDay()
        data[dayIndex].revenue += p.amount
        data[dayIndex].count += 1
      })
      return data
    } else if (period === 'month' || period === 'lastmonth') {
      const weeks = [
        { label: 'Week 1', revenue: 0, count: 0 },
        { label: 'Week 2', revenue: 0, count: 0 },
        { label: 'Week 3', revenue: 0, count: 0 },
        { label: 'Week 4', revenue: 0, count: 0 },
        { label: 'Week 5', revenue: 0, count: 0 },
      ]
      periodPayments.forEach(p => {
        const dayOfMonth = p.date.getDate()
        const weekIndex = Math.min(4, Math.floor((dayOfMonth - 1) / 7))
        weeks[weekIndex].revenue += p.amount
        weeks[weekIndex].count += 1
      })
      return weeks.filter(w => w.revenue > 0 || w.count > 0)
    }

    const months = {}
    periodPayments.forEach(p => {
      const month = p.date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
      if (!months[month]) months[month] = { label: month, revenue: 0, count: 0 }
      months[month].revenue += p.amount
      months[month].count += 1
    })
    return Object.values(months)
  }, [allPayments, currentRange, period])

  // Top Clients based on PAYMENTS in period
  const topClients = useMemo(() => {
    const periodPayments = allPayments.filter(p => paymentsByRange(p, currentRange))
    const clientStats = {}

    periodPayments.forEach(p => {
      if (!clientStats[p.clientId]) {
        const client = getClientById(p.clientId)
        clientStats[p.clientId] = {
          name: client?.name || 'Unknown',
          businessName: client?.businessName,
          totalPaid: 0,
          paymentCount: 0
        }
      }
      clientStats[p.clientId].totalPaid += p.amount
      clientStats[p.clientId].paymentCount += 1
    })

    return Object.values(clientStats)
      .sort((a, b) => b.totalPaid - a.totalPaid)
      .slice(0, 5)
  }, [allPayments, currentRange, getClientById])

  // Aging buckets (real-time snapshot)
  const agingData = useMemo(() => {
    const buckets = [
      { label: 'Current', amount: 0, count: 0, color: 'green' },
      { label: '1-30 Days', amount: 0, count: 0, color: 'yellow' },
      { label: '31-60 Days', amount: 0, count: 0, color: 'orange' },
      { label: '60+ Days', amount: 0, count: 0, color: 'red' },
    ]

    invoices.forEach(inv => {
      if (inv.status === 'paid' || inv.status === 'draft') return

      const dueDate = parseDate(inv.dueDate)
      if (!dueDate) return

      const daysPast = Math.floor((new Date() - dueDate) / (1000 * 60 * 60 * 24))
      const balance = inv.total - (inv.partialAmount || 0)

      if (daysPast <= 0) {
        buckets[0].amount += balance
        buckets[0].count++
      } else if (daysPast <= 30) {
        buckets[1].amount += balance
        buckets[1].count++
      } else if (daysPast <= 60) {
        buckets[2].amount += balance
        buckets[2].count++
      } else {
        buckets[3].amount += balance
        buckets[3].count++
      }
    })
    return buckets
  }, [invoices])

  return (
    <div className="space-y-6 animate-fade-in pb-24 md:pb-6 font-sans">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Reports & Analytics</h2>
            <p className="text-gray-600 mt-1 text-sm">{currentRange.label} Insights</p>
          </div>
          <button className="btn btn-secondary btn-touch" onClick={() => window.print()}>
            <Download size={18} />
            Download
          </button>
        </div>

        {/* Period Selector */}
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide">
          {periodOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all border ${period === opt.value
                ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                : 'bg-white text-gray-600 border-gray-200 hover:border-primary-400'
                }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <MetricCard
          icon={DollarSign}
          iconBg="bg-green-100"
          iconColor="text-green-600"
          label="Revenue"
          value={`$${metrics.revenue.toLocaleString()}`}
          change={getChange(metrics.revenue, prevMetrics?.revenue)}
          subtext="Money received"
        />
        <MetricCard
          icon={FileText}
          iconBg="bg-blue-100"
          iconColor="text-blue-600"
          label="Total Billed"
          value={`$${metrics.billed.toLocaleString()}`}
          change={getChange(metrics.billed, prevMetrics?.billed)}
          subtext={`${metrics.invoiceCount} invoices`}
        />
        <MetricCard
          icon={Clock}
          iconBg="bg-primary-100"
          iconColor="text-primary-600"
          label="Outstanding"
          value={`$${globalMetrics.outstanding.toLocaleString()}`}
          subtext="Total balance due"
        />
        <MetricCard
          icon={AlertCircle}
          iconBg="bg-red-100"
          iconColor="text-red-600"
          label="Overdue"
          value={`$${globalMetrics.overdue.toLocaleString()}`}
          valueColor="text-red-600"
          subtext="Unpaid past due"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Revenue Breakdown */}
        <div className="card p-4 md:p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-gray-900">Revenue Breakdown</h3>
            <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400 bg-gray-50 px-2 py-1 rounded">
              {period.includes('week') ? 'Daily' : period.includes('month') ? 'Weekly' : 'Monthly'}
            </span>
          </div>

          {breakdownData.length === 0 ? (
            <div className="h-48 flex items-center justify-center bg-gray-50 rounded-xl border border-dashed border-gray-200">
              <div className="text-center">
                <BarChart3 size={32} className="mx-auto text-gray-300 mb-2" />
                <p className="text-gray-400 text-sm">No revenue recorded</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {breakdownData.map((item, index) => {
                const maxAmount = Math.max(...breakdownData.map(d => d.revenue))
                const percentage = maxAmount > 0 ? (item.revenue / maxAmount) * 100 : 0

                return (
                  <div key={index} className="flex items-center gap-3">
                    <span className="text-xs font-semibold text-gray-500 w-12 shrink-0">{item.label}</span>
                    <div className="flex-1 h-5 bg-gray-50 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary-600 rounded-full transition-all duration-700 ease-out"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className="text-xs font-bold text-gray-900 w-24 text-right">
                      ${item.revenue.toLocaleString()}
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Top Clients */}
        <div className="card p-4 md:p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-gray-900">Top Clients</h3>
            <span className="text-xs text-primary-600 font-medium">by revenue</span>
          </div>

          {topClients.length === 0 ? (
            <div className="h-48 flex items-center justify-center bg-gray-50 rounded-xl border border-dashed border-gray-200">
              <div className="text-center">
                <Users size={32} className="mx-auto text-gray-300 mb-2" />
                <p className="text-gray-400 text-sm">No client activity</p>
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              {topClients.map((client, index) => {
                const maxRevenue = topClients[0].totalPaid
                const percentage = (client.totalPaid / maxRevenue) * 100

                return (
                  <div key={index} className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center text-xs font-bold text-gray-400 shrink-0">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-sm font-bold text-gray-900 truncate">{client.businessName || client.name}</p>
                        <span className="text-xs font-bold text-primary-600">
                          ${client.totalPaid.toLocaleString()}
                        </span>
                      </div>
                      <div className="h-2 bg-gray-50 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary-500 rounded-full transition-all duration-700 ease-out"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Aging Report Card */}
      <div className="card overflow-hidden">
        <div className="px-4 md:px-6 py-4 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Aging Report</h3>
            <p className="text-xs text-gray-500">Unpaid balances by age</p>
          </div>
          <span className="px-2 py-1 bg-white border border-gray-200 rounded text-[10px] font-bold text-gray-400 uppercase tracking-tight">Active Balance</span>
        </div>

        <div className="p-4 md:p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {agingData.map((bucket, index) => (
              <div key={index} className={`p-4 rounded-xl border-2 ${bucket.color === 'green' ? 'bg-green-50/30 border-green-100 text-green-700' :
                bucket.color === 'yellow' ? 'bg-yellow-50/30 border-yellow-100 text-yellow-700' :
                  bucket.color === 'orange' ? 'bg-orange-50/30 border-orange-100 text-orange-700' :
                    'bg-red-50/30 border-red-100 text-red-700'
                }`}>
                <p className="text-[10px] font-bold uppercase tracking-wider mb-1 opacity-70">{bucket.label}</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-lg font-black">${bucket.amount.toLocaleString()}</span>
                </div>
                <p className="text-[10px] font-medium opacity-60 mt-0.5">{bucket.count} invoices</p>
              </div>
            ))}
          </div>

          {/* Quick List of Overdue */}
          <div className="mt-8">
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Urgent Attention</h4>
            <div className="space-y-2">
              {invoices
                .filter(inv => inv.status === 'overdue' || (inv.status !== 'paid' && inv.status !== 'draft' && parseDate(inv.dueDate) < new Date()))
                .sort((a, b) => (parseDate(a.dueDate) || 0) - (parseDate(b.dueDate) || 0))
                .slice(0, 3)
                .map((inv) => {
                  const client = getClientById(inv.clientId)
                  const dueDate = parseDate(inv.dueDate)
                  const daysPast = dueDate ? Math.floor((new Date() - dueDate) / (1000 * 60 * 60 * 24)) : 0

                  return (
                    <div key={inv.id} className="group p-3 hover:bg-gray-50 rounded-xl transition-colors border border-transparent hover:border-gray-100 flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-red-600 shrink-0">
                          <AlertCircle size={16} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-bold text-gray-900 text-sm truncate">{inv.invoiceNumber}</p>
                          <p className="text-xs text-gray-500 truncate">{client?.name || 'Unknown'}</p>
                        </div>
                      </div>
                      <div className="text-right shrink-0 ml-4">
                        <p className="font-black text-gray-900 text-sm">
                          ${(inv.total - (inv.partialAmount || 0)).toLocaleString()}
                        </p>
                        <p className="text-[10px] font-bold text-red-600 uppercase">
                          {daysPast > 0 ? `${daysPast}d overdue` : 'due today'}
                        </p>
                      </div>
                    </div>
                  )
                })}
              {invoices.filter(inv => (inv.status === 'overdue' || (inv.status !== 'paid' && inv.status !== 'draft' && parseDate(inv.dueDate) < new Date()))).length === 0 && (
                <div className="py-8 text-center bg-gray-50/50 rounded-2xl border border-dashed border-gray-200">
                  <CheckCircle2 size={32} className="mx-auto text-green-400 mb-2" />
                  <p className="text-xs font-medium text-gray-400">All invoices are up to date</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function MetricCard({ icon: Icon, iconBg, iconColor, label, value, valueColor = 'text-gray-900', change, changeInverse, subtext }) {
  const isPositive = change && parseFloat(change) > 0
  const isNegative = change && parseFloat(change) < 0

  const showGreen = changeInverse ? isNegative : isPositive
  const showRed = changeInverse ? isPositive : isNegative

  return (
    <div className="card p-4 hover:shadow-md transition-shadow">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center shrink-0 shadow-sm border border-white`}>
          <Icon size={18} className={iconColor} />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider truncate mb-0.5">{label}</p>
          <p className={`text-lg md:text-xl font-black ${valueColor} truncate leading-none`}>{value}</p>
        </div>
      </div>
      <div className="flex items-center justify-between pt-2 border-t border-gray-50">
        {change !== null ? (
          <div className={`flex items-center gap-1 text-[10px] font-bold ${showGreen ? 'text-green-600' : showRed ? 'text-red-600' : 'text-gray-500'}`}>
            {showGreen ? <TrendingUp size={10} /> : showRed ? <TrendingDown size={10} /> : null}
            <span>{isPositive ? '+' : ''}{change}%</span>
          </div>
        ) : <div />}
        {subtext && <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">{subtext}</span>}
      </div>
    </div>
  )
}
